import { useCallback, useState } from 'react';
import { type ICallAccount } from '@/types/ICallAccount';
import { type TCallOrdersFilterKind } from '@/types/ICallOrdersFilter';
import { getCallAccountKey } from '@/utils/isCallAccount';
import { useCallOrders, type IUseCallOrdersResult } from './useCallOrders';

const TAB_LABELS: Record<TCallOrdersFilterKind, string> = {
  client: 'Заказы клиента',
  recipient: 'Заказы по номеру'
};

const EMPTY_TEXTS: Record<TCallOrdersFilterKind, string> = {
  client: 'У клиента пока нет заказов.',
  recipient: 'Заказов с этим номером получателя не найдено.'
};

/** Описание таба для полосы вкладок. */
export interface ICallOrdersTab {
  kind: TCallOrdersFilterKind;
  label: string;
  /** Счётчик заказов; `null` — выборка ещё не загружалась. */
  count: number | null;
  /** Идёт первая загрузка выборки — счётчику пока нечего показывать. */
  isCountLoading: boolean;
}

interface IUseCallOrdersTabsParams {
  /** Аккаунт звонящего; `null` — номер не привязан ни к одному аккаунту. */
  account: ICallAccount | null;
  /** Номер звонящего, только цифры. */
  phone: string;
  isModalOpen: boolean;
}

interface IUseCallOrdersTabsResult {
  /** Табы в порядке отображения: при звонке без аккаунта остаётся один. */
  tabs: ICallOrdersTab[];
  activeKind: TCallOrdersFilterKind;
  /** Данные активного таба. */
  activeOrders: IUseCallOrdersResult;
  /** Текст пустого состояния активного таба. */
  activeEmptyText: string;
  /** Активна выборка по номеру — таблице нужна колонка «Клиент». */
  showClient: boolean;
  /**
   * Ключ учётки звонящего — `uuid`, у прежнего бэкенда `id` (PAR-5189) —
   * либо `null`. Для метки «Заказ клиента».
   */
  accountId: string | number | null;
  changeTab: (kind: TCallOrdersFilterKind) => void;
}

/**
 * Управляет двумя выборками заказов в модалке звонка: заказы аккаунта клиента
 * и заказы, где номер звонящего указан получателем.
 *
 * Обе выборки грузятся сразу при открытии модалки: счётчики табов должны быть
 * видны до клика, иначе менеджер не понимает объём второго списка. Данные
 * табов живут независимо, поэтому переключение туда-обратно не перезапрашивает
 * их и не сбрасывает страницу.
 *
 * Каждое новое открытие модалки (и смена звонка) возвращает активный таб к
 * исходному и перезапрашивает данные: между двумя звонками клиент мог
 * оформить заказ.
 *
 * @param params.account - аккаунт звонящего либо `null`
 * @param params.phone - номер звонящего, только цифры
 * @param params.isModalOpen - открыта ли модалка
 * @returns табы со счётчиками, активный таб, его данные и переключение
 */
export const useCallOrdersTabs = ({
  account,
  phone,
  isModalOpen
}: IUseCallOrdersTabsParams): IUseCallOrdersTabsResult => {
  // Ключ учётки — `uuid`, запасной — `id` прежнего бэкенда (PAR-5189).
  const clientId = getCallAccountKey(account);
  const hasClientTab = clientId !== null;
  const defaultKind: TCallOrdersFilterKind = hasClientTab
    ? 'client'
    : 'recipient';

  // Сессия модалки: смена звонка или переоткрытие возвращает табы в исходное
  // состояние. Корректировка прямо в рендере (приём из документации React) —
  // иначе первый кадр показал бы таб от предыдущего звонка.
  const sessionKey = `${phone}:${String(clientId)}:${String(isModalOpen)}`;
  const [lastSessionKey, setLastSessionKey] = useState<string>(sessionKey);
  const [selectedKind, setSelectedKind] =
    useState<TCallOrdersFilterKind>(defaultKind);

  if (sessionKey !== lastSessionKey) {
    setLastSessionKey(sessionKey);
    setSelectedKind(defaultKind);
  }

  // Таба заказов клиента может не быть — активным считаем только допустимый.
  const activeKind: TCallOrdersFilterKind = hasClientTab
    ? selectedKind
    : 'recipient';

  const clientOrders = useCallOrders({
    filter: clientId === null ? null : { kind: 'client', clientId },
    enabled: isModalOpen && clientId !== null
  });

  const recipientOrders = useCallOrders({
    filter: phone === '' ? null : { kind: 'recipient', phone },
    enabled: isModalOpen
  });

  const changeTab = useCallback((kind: TCallOrdersFilterKind): void => {
    setSelectedKind(kind);
  }, []);

  const buildTab = (
    kind: TCallOrdersFilterKind,
    orders: IUseCallOrdersResult
  ): ICallOrdersTab => ({
    kind,
    label: TAB_LABELS[kind],
    // На ошибке счётчик молчит: нуль там означал бы «заказов нет».
    count: orders.hasLoaded && orders.error === null ? orders.total : null,
    isCountLoading: orders.isLoading && !orders.hasLoaded
  });

  const tabs: ICallOrdersTab[] = [
    ...(hasClientTab ? [buildTab('client', clientOrders)] : []),
    buildTab('recipient', recipientOrders)
  ];

  return {
    tabs,
    activeKind,
    activeOrders: activeKind === 'client' ? clientOrders : recipientOrders,
    activeEmptyText: EMPTY_TEXTS[activeKind],
    showClient: activeKind === 'recipient',
    accountId: clientId,
    changeTab
  };
};
